import { useEffect, useState } from 'react';
import { apiClient } from '@/shared/api/client';
import { useAuthStore } from '@/shared/stores/auth';

export interface HeartbeatResponse {
  now: string;
  presence: { status: string; changed_at: string | null };
  active_call: {
    id: number;
    direction: 'inbound' | 'outbound';
    status: string;
    from: string;
    to: string;
    started_at: string;
    answered: boolean;
  } | null;
  incoming: { call_id?: number | null; from: string; channel?: string; did?: string; via?: string } | null;
  last_missed?: { from: string; did?: string; status: string; at: string } | null;
  unread_notifications: number;
}

/**
 * Polls /agent/heartbeat every `intervalMs`.
 *
 * This runs even while the tab is in the BACKGROUND, on purpose. The heartbeat
 * is what keeps the agent from being marked offline: the server sweeps presence
 * every minute and flips any row older than 90s to "offline". An agent who
 * leaves the softphone open in a background tab is still at their desk and must
 * stay dialable — pausing the beat here was silently downing them, and the next
 * dial then behaved as if they were offline.
 *
 * Note this is a presence keep-alive, not a data poll: screens that only render
 * data still use `usePolling`, which DOES pause while hidden (correctly — there
 * is nobody looking at it). The payload is cheap and the interval is 30s, so
 * running it hidden costs one small request per half-minute.
 *
 * Returns the latest payload and a `lastError`.
 */
export function useHeartbeat(intervalMs: number = 30_000): {
  data: HeartbeatResponse | null;
  lastError: Error | null;
} {
  const token = useAuthStore((s) => s.token);
  const persona = useAuthStore((s) => s.persona);
  const [data, setData] = useState<HeartbeatResponse | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);

  useEffect(() => {
    if (!token || persona !== 'agent') return;

    let active = true;
    let timer: number | undefined;

    async function tick() {
      // No document.hidden guard — see the note above: presence must survive a
      // background tab or the server sweeps the agent offline.
      if (!active) return;
      try {
        const { data: resp } = await apiClient.post<{ success: boolean; data: HeartbeatResponse }>(
          '/agent/heartbeat'
        );
        if (active) {
          setData(resp.data);
          setLastError(null);
        }
      } catch (e) {
        if (active) setLastError(e as Error);
      }
    }

    void tick();
    timer = window.setInterval(tick, intervalMs);
    const onVisible = () => {
      if (!document.hidden) void tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      if (timer !== undefined) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, persona, intervalMs]);

  return { data, lastError };
}
